/**
 * 📈 New-player funnel counter, client half (v7.39, Javi's D13). One call
 * per funnel moment - `track('welcome_yes')` - and the server adds 1 to
 * today's count for that step, split by device size and build. Nothing
 * else leaves the device: no uid, no device id, no cookie, no user agent.
 * See server/api/funnel.post.ts for exactly what is stored.
 *
 * Rules this file keeps (pinned in tests/run-sync.test.ts):
 *   - it never imports or boots Firebase - a visitor who bounces before
 *     the lazy boot (app/plugins/firebase.client.ts: first gesture or 8 s)
 *     is exactly the visitor worth counting, so this is a plain fetch to a
 *     route App Check exempts;
 *   - it never throws, never awaits, never blocks: fire and forget;
 *   - it does nothing without a window (SSR/prerender), in the test
 *     harnesses (verify.mjs and tests/*.mjs seed the wallet LOCAL-MODE
 *     flag - the step lands in `window.__edFunnel` instead, so a browser
 *     suite can assert it), under automation (navigator.webdriver) or for
 *     crawlers, so none of those pollute the production counts.
 *
 * `once: true` = at most once per DEVICE (first-time steps such as
 * title_first_view): the sent names live in FUNNEL_SENT_KEY, which a
 * progress wipe preserves on purpose (app/utils/progressEpoch.ts).
 * The first once-step on a device also decides its cohort: a device that
 * already has progress is a returning player and never counts one (see
 * claimOnce).
 */
import { CURRENT_VERSION } from '../data/version'
import { apiUrl } from './api'
import {
  FUNNEL_MESSAGE_MAX, type FunnelEvent, type FunnelSize, type FunnelStep, type FunnelWhere,
  isFunnelStep, scrubFunnelMessage,
} from '../../shared/funnel'

export const FUNNEL_SENT_KEY = 'elemental-dispatch-funnel-v1'
/** 🎓 v7.39 (per Javi): the once-list entry that marks a returning player's
 * device (never a step name - steps are snake_case). */
export const FUNNEL_VETERAN = '~veteran'
/** The harnesses' marker (useStardust's LOCAL MODE switch). */
const HARNESS_KEY = 'elemental-dispatch-wallet-local'
/** Nuxt's own crawler test (NuxtRoot), plus the usual headless tells.
 * 🎓 v7.39: `bot` not after a `u` - Cubot is an Android phone brand
 * ("CUBOT P50"); no lookbehind, which Safari before 16.4 cannot parse. */
const BOT_RE = /(?:^|[^u])bot\b|crawler|spider|chrome-lighthouse|headlesschrome|facebookexternalhit|google\b/i

export interface TrackOpts {
  /** At most once per device. */
  once?: boolean
  /** Error beacon only. */
  message?: string
  where?: FunnelWhere
}

/**
 * Device class from the VIEWPORT (what the layout actually answered to)
 * plus the primary pointer (what tells a short laptop window from a phone):
 *   - phone:   shortest side < 600 CSS px on a touch-first device - every
 *              phone, portrait or landscape (landscape phones are ~390 tall);
 *   - tablet:  shortest side 600-1099 on a touch-first device - iPad mini
 *              (744) through iPad Pro 13" (1032), Android tablets;
 *   - desktop: a fine pointer at any size (a 1280x580 laptop viewport is
 *              not a phone), or a touch screen 1100+ on its short side.
 * `coarse` null = the browser cannot say: under 600 reads as a phone,
 * anything wider as a desktop.
 */
export function funnelSize(width: number, height: number, coarse: boolean | null): FunnelSize {
  const short = Math.min(width, height)
  if (coarse === false) return 'desktop'
  if (short < 600) return 'phone'
  if (coarse && short < 1100) return 'tablet'
  return 'desktop'
}

type Env = typeof globalThis & {
  __edFunnel?: FunnelEvent[]
  navigator?: { webdriver?: boolean; userAgent?: string }
}

/** Why this device must not send (null = it may). */
export function funnelSkipReason(): 'harness' | 'automation' | 'bot' | null {
  const g = globalThis as Env
  try {
    if (g.localStorage?.getItem(HARNESS_KEY) === '1') return 'harness'
  } catch { /* no storage: not a harness either */ }
  if (g.navigator?.webdriver === true) return 'automation'
  if (BOT_RE.test(g.navigator?.userAgent ?? '')) return 'bot'
  return null
}

// Once-steps already sent in THIS page load - the fallback when storage
// is unavailable (private mode), so a repeated call still sends once.
const sentThisLoad = new Set<string>()

/** The device's once-list; null = never written (its cohort is undecided). */
function readSent(): string[] | null {
  try {
    const raw = globalThis.localStorage?.getItem(FUNNEL_SENT_KEY)
    if (raw == null) return null
    const list: unknown = JSON.parse(raw)
    return Array.isArray(list) ? list.filter((s): s is string => typeof s === 'string') : []
  } catch {
    return []
  }
}

/** 🎓 v7.39 (per Javi): progress a first-timer cannot have yet - a finished
 * tutorial, a saved run, an ascension, a lifetime run, a Puzzle Game best.
 * Read by value: the title's loaders may write empty defaults before the
 * first step is claimed. */
export function deviceHasProgress(): boolean {
  const get = (key: string): string | null => {
    try { return globalThis.localStorage?.getItem(key) ?? null } catch { return null }
  }
  const json = (key: string): unknown => {
    try { return JSON.parse(get(key) ?? 'null') } catch { return null }
  }
  const saved = (key: string) => { const raw = get(key); return !!raw && raw !== 'null' }
  if (get('elemental-dispatch-tutorial-done') === '1') return true
  if (saved('elemental-dispatch-run-v6') || saved('elemental-dispatch-daily-v6')) return true
  if (Number(get('elemental-dispatch-ascension-unlocked')) > 0) return true
  const cleared = json('elemental-dispatch-ascension-cleared')
  if (Array.isArray(cleared) && cleared.length > 0) return true
  const lifetime = json('elemental-dispatch-lifetime-v1') as { runs?: Record<string, { started?: unknown } | null> } | null
  if (lifetime?.runs && typeof lifetime.runs === 'object'
    && Object.values(lifetime.runs).some(tally => Number(tally?.started) > 0)) return true
  const puzzle = json('elemental-dispatch-puzzle-v1')
  return !!puzzle && typeof puzzle === 'object' && Object.keys(puzzle).length > 0
}

/** True the first time `step` is claimed on this device, false after. */
function claimOnce(step: FunnelStep): boolean {
  if (sentThisLoad.has(step)) return false
  let sent = readSent()
  if (sent === null) {
    // 🎓 v7.39 (per Javi): the device's first once-step decides its cohort.
    // Progress already here = a returning player, not a first-timer: mark
    // the device and count nothing on it, ever (the list outlives a wipe).
    if (deviceHasProgress()) {
      try {
        globalThis.localStorage?.setItem(FUNNEL_SENT_KEY, JSON.stringify([FUNNEL_VETERAN]))
      } catch { /* re-decided (the same way) on the next call */ }
      return false
    }
    sent = []
  }
  if (sent.includes(FUNNEL_VETERAN)) return false
  sentThisLoad.add(step)
  if (sent.includes(step)) return false
  try {
    globalThis.localStorage?.setItem(FUNNEL_SENT_KEY, JSON.stringify([...sent, step]))
  } catch { /* the in-memory set still holds it for this load */ }
  return true
}

function currentSize(): FunnelSize {
  const w = Number(globalThis.innerWidth) || 0
  const h = Number(globalThis.innerHeight) || 0
  let coarse: boolean | null = null
  try {
    if (typeof globalThis.matchMedia === 'function') coarse = globalThis.matchMedia('(pointer: coarse)').matches
  } catch { /* leave it to the width */ }
  return funnelSize(w, h, coarse)
}

/** The run fields the step gates read (a structural RunState slice). */
export interface FunnelRun {
  mode?: string
  gauntlet?: boolean
  puzzleLevel?: number
  tutorialSeen?: string[]
}

/** 🎓 v7.39 (per Javi): the road a first-timer takes is the campaign.
 * The weekly (mode 'daily'), the Gauntlet, Puzzle Game sessions and the
 * practice (mode 'tutorial', counted by its own practice_* steps) never
 * count toward the run steps - any mode added later stays out too. */
export function funnelCampaign(run: FunnelRun | null | undefined): boolean {
  return !!run && !run.mode && !run.gauntlet && typeof run.puzzleLevel !== 'number'
}

/** 🎓 v7.39 (per Javi): the practice, but not a replay of it - a
 * player with a run of their own taking it again ('guide:replay'), or one
 * whose tutorial is already done re-ticking the title's guide (the start
 * marks that run 'guide:reward-claimed'), is not a first-timer. A
 * first-timer's run only gets that mark after finishFirstDispatch counts. */
export function funnelPractice(run: FunnelRun | null | undefined): boolean {
  return run?.mode === 'tutorial'
    && !run.tutorialSeen?.some(id => id === 'guide:replay' || id === 'guide:reward-claimed')
}

/** Count one funnel step. Fire and forget: never throws, never waits. */
export function track(step: FunnelStep, opts: TrackOpts = {}): void {
  try {
    if (typeof window === 'undefined' || typeof fetch !== 'function') return
    if (!isFunnelStep(step)) return
    if (opts.once && !claimOnce(step)) return
    const ev: FunnelEvent = { step, build: CURRENT_VERSION, size: currentSize() }
    if (step === 'error') {
      ev.message = scrubFunnelMessage(opts.message).slice(0, FUNNEL_MESSAGE_MAX)
      ev.where = opts.where ?? 'onerror'
    }
    const skip = funnelSkipReason()
    if (skip) {
      if (skip === 'harness') {
        const g = globalThis as Env
        ;(g.__edFunnel ??= []).push(ev)
      }
      return
    }
    // keepalive: a step sent as the page goes away (welcome_closed, a
    // bounce) still arrives. No App Check header - the route is exempt.
    // 📱 M3: apiUrl - the apps send it to the hosted API (utils/api.ts).
    void fetch(apiUrl('/api/funnel'), {
      method: 'POST',
      keepalive: true,
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(ev),
    }).catch(() => { /* counting is best-effort */ })
  } catch { /* never let counting break the game */ }
}
